import { resolveFishipediaEntry } from "./fishipediaResolver";

const DETAILS_JSON_PATHS = {
  freshwater: "/fish-species/details/freshwater-details.json",
  marine: "/fish-species/details/marine-details.json",
  aliases: "/fish-species/details/species-aliases.json",
};
const FISH_DATA_JSON_PATH = "/JSON/fish-data.json";

const FISHIPEDIA_MARKDOWN_LANGS = ["en", "bg"];
const HTML_IMAGE_SRC_REGEX = /<img\b[^>]*\bsrc=["']([^"']+)["'][^>]*>/i;
const MARKDOWN_IMAGE_SRC_REGEX =
  /!\[[^\]]*]\(([^)\s]+)(?:\s+["'][^"']*["'])?\)/i;

let fishDetailsCatalogPromise = null;
let fishSpeciesListPromise = null;
const fishipediaImagePromiseBySlug = new Map();

function normalizeLookupValue(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function normalizeDisplayValue(value) {
  return normalizeLookupValue(value).replace(/[^a-z0-9]+/g, "");
}

function hasParentheticalText(value) {
  return /\([^)]+\)/.test(String(value || ""));
}

function includesScientificName(displayName, scientificName) {
  const normalizedDisplayName = normalizeDisplayValue(displayName);
  const normalizedScientificName = normalizeDisplayValue(scientificName);

  return (
    Boolean(normalizedDisplayName) &&
    Boolean(normalizedScientificName) &&
    normalizedDisplayName.includes(normalizedScientificName)
  );
}

function addLookupId(targetMap, rawValue, speciesId) {
  const normalizedValue = normalizeLookupValue(rawValue);
  if (!normalizedValue || !speciesId) {
    return;
  }

  const existing = targetMap.get(normalizedValue);

  if (existing) {
    existing.add(speciesId);
    return;
  }

  targetMap.set(normalizedValue, new Set([speciesId]));
}

function addCandidateValue(target, rawValue) {
  if (typeof rawValue !== "string") {
    return;
  }

  const trimmedValue = rawValue.trim();
  if (!trimmedValue || target.includes(trimmedValue)) {
    return;
  }

  target.push(trimmedValue);
}

function collectCandidateNames(input) {
  const values = [];

  if (typeof input === "object" && input !== null) {
    addCandidateValue(values, input.id);
    addCandidateValue(values, input.name);
    addCandidateValue(values, input.commonName);
    addCandidateValue(values, input.common_name);
    addCandidateValue(values, input.scientificName);
    addCandidateValue(values, input.displayName);

    if (Array.isArray(input.aliases)) {
      input.aliases.forEach((alias) => addCandidateValue(values, alias));
    }
  } else {
    addCandidateValue(values, input);
  }

  values.slice().forEach((value) => {
    if (value.includes("/")) {
      value.split(/\s*\/\s*/).forEach((part) => addCandidateValue(values, part));
    }

    const parentheticalMatch = value.match(/^(.+?)\s*\(([^)]+)\)$/);
    if (!parentheticalMatch) {
      return;
    }

    addCandidateValue(values, parentheticalMatch[1]);
    addCandidateValue(values, parentheticalMatch[2]);
  });

  return values;
}

function pickDetailForWaterType(entries, waterType) {
  if (!Array.isArray(entries) || entries.length === 0) {
    return null;
  }

  if (waterType) {
    const preferredMatch = entries.find((entry) => entry?.waterType === waterType);
    if (preferredMatch) {
      return preferredMatch;
    }
  }

  return entries[0] ?? null;
}

async function fetchJson(path) {
  const response = await fetch(path);

  if (!response.ok) {
    throw new Error(`Failed to load ${path}: ${response.status}`);
  }

  return response.json();
}

async function fetchText(path) {
  const response = await fetch(path);

  if (!response.ok) {
    throw new Error(`Failed to load ${path}: ${response.status}`);
  }

  return response.text();
}

function buildFishDetailsCatalog(freshwaterJson, marineJson, aliasesJson) {
  const bySpeciesId = new Map();
  const byLookupName = new Map();
  const canonicalSpeciesIdById = new Map();

  const canonicalGroups = Array.isArray(aliasesJson?.canonicalGroups)
    ? aliasesJson.canonicalGroups
    : [];

  canonicalGroups.forEach((group) => {
    const preferredId = group?.preferredId;
    const ids = Array.isArray(group?.ids) ? group.ids : [];

    ids.forEach((id) => {
      if (id && preferredId) {
        canonicalSpeciesIdById.set(id, preferredId);
      }
    });
  });

  function registerDetailEntries(detailsJson) {
    const speciesRecord = detailsJson?.species;
    if (!speciesRecord || typeof speciesRecord !== "object") {
      return;
    }

    Object.values(speciesRecord).forEach((entry) => {
      const speciesId = entry?.id;
      if (!speciesId) {
        return;
      }

      const existingEntries = bySpeciesId.get(speciesId) ?? [];
      bySpeciesId.set(speciesId, [...existingEntries, entry]);

      addLookupId(byLookupName, speciesId, speciesId);
      addLookupId(byLookupName, entry.commonName, speciesId);
      addLookupId(byLookupName, entry.scientificName, speciesId);
      addLookupId(byLookupName, entry.displayName, speciesId);

      if (Array.isArray(entry.aliases)) {
        entry.aliases.forEach((alias) => addLookupId(byLookupName, alias, speciesId));
      }
    });
  }

  registerDetailEntries(freshwaterJson);
  registerDetailEntries(marineJson);

  const displayNameAliases = aliasesJson?.displayNameAliases;
  if (displayNameAliases && typeof displayNameAliases === "object") {
    Object.entries(displayNameAliases).forEach(([speciesId, aliasValues]) => {
      if (!Array.isArray(aliasValues)) {
        return;
      }

      aliasValues.forEach((aliasValue) => addLookupId(byLookupName, aliasValue, speciesId));
    });
  }

  return {
    bySpeciesId,
    byLookupName,
    canonicalSpeciesIdById,
  };
}

async function loadFishDetailsCatalog() {
  if (!fishDetailsCatalogPromise) {
    fishDetailsCatalogPromise = Promise.all([
      fetchJson(DETAILS_JSON_PATHS.freshwater),
      fetchJson(DETAILS_JSON_PATHS.marine),
      fetchJson(DETAILS_JSON_PATHS.aliases),
    ]).then(([freshwaterJson, marineJson, aliasesJson]) =>
      buildFishDetailsCatalog(freshwaterJson, marineJson, aliasesJson)
    );
  }

  return fishDetailsCatalogPromise;
}

async function loadFishSpeciesList() {
  if (!fishSpeciesListPromise) {
    fishSpeciesListPromise = fetchJson(FISH_DATA_JSON_PATH).then((data) =>
      Array.isArray(data) ? data : []
    );
  }

  return fishSpeciesListPromise;
}

function findFishSpeciesListEntry(fishList, fish) {
  const candidates = collectCandidateNames(fish).map(normalizeLookupValue);

  if (!candidates.length || !Array.isArray(fishList)) {
    return null;
  }

  return (
    fishList.find((entry) => {
      if (!entry || typeof entry !== "object") {
        return false;
      }

      return [
        entry.id,
        entry.commonName,
        entry.common_name,
        entry.name,
        entry.scientificName,
        entry.scientific_name,
      ]
        .map(normalizeLookupValue)
        .some((value) => value && candidates.includes(value));
    }) || null
  );
}

function resolveSpeciesDetail(catalog, fish, waterType) {
  const candidateIds = [];
  const candidateNames = collectCandidateNames(fish);

  function pushCandidateId(speciesId) {
    if (!speciesId) {
      return;
    }

    const canonicalId = catalog.canonicalSpeciesIdById.get(speciesId) ?? speciesId;

    if (!candidateIds.includes(canonicalId)) {
      candidateIds.push(canonicalId);
    }
  }

  if (typeof fish === "object" && fish !== null) {
    pushCandidateId(fish.id);
  }

  candidateNames.forEach((candidateName) => {
    const matchedSpeciesIds = catalog.byLookupName.get(normalizeLookupValue(candidateName));
    if (!matchedSpeciesIds) {
      return;
    }

    matchedSpeciesIds.forEach((speciesId) => pushCandidateId(speciesId));
  });

  for (const candidateId of candidateIds) {
    const detailEntry = pickDetailForWaterType(
      catalog.bySpeciesId.get(candidateId),
      waterType
    );

    if (detailEntry) {
      return detailEntry;
    }
  }

  return null;
}

function extractFirstImageSrc(markdownText) {
  if (!markdownText) {
    return null;
  }

  const htmlMatch = markdownText.match(HTML_IMAGE_SRC_REGEX);
  if (htmlMatch?.[1]) {
    return htmlMatch[1];
  }

  const markdownMatch = markdownText.match(MARKDOWN_IMAGE_SRC_REGEX);
  return markdownMatch?.[1] ?? null;
}

async function resolveFishipediaImageSrcForSlug(slug) {
  if (!slug) {
    return null;
  }

  if (!fishipediaImagePromiseBySlug.has(slug)) {
    fishipediaImagePromiseBySlug.set(
      slug,
      (async () => {
        for (const language of FISHIPEDIA_MARKDOWN_LANGS) {
          try {
            const markdown = await fetchText(`/Markdown/fishipedia/${slug}/${language}.md`);
            const imageSrc = extractFirstImageSrc(markdown);

            if (imageSrc) {
              return imageSrc;
            }
          } catch {
            // Best-effort only. We keep the modal's blank-image fallback.
          }
        }

        return null;
      })()
    );
  }

  return fishipediaImagePromiseBySlug.get(slug);
}

function resolveFishipediaMatchTarget(fish, detail) {
  if (!detail) {
    return fish;
  }

  const imageConfig = detail.image && typeof detail.image === "object" ? detail.image : null;

  return {
    id: detail.id,
    name: detail.displayName,
    commonName: imageConfig?.matchCommonName || detail.commonName,
    scientificName: imageConfig?.matchScientificName || detail.scientificName,
    displayName: detail.displayName,
    aliases: [
      ...(Array.isArray(detail.aliases) ? detail.aliases : []),
      imageConfig?.matchCommonName,
      imageConfig?.matchScientificName,
    ].filter(Boolean),
  };
}

export async function resolveFishSpeciesDisplayParts(fish, options = {}) {
  const fallbackName =
    typeof fish === "object" && fish !== null
      ? fish.displayName || fish.commonName || fish.name || ""
      : fish;
  const commonName = typeof fallbackName === "string" ? fallbackName.trim() : "";

  if (!commonName) {
    return null;
  }

  if (hasParentheticalText(commonName)) {
    return {
      commonName,
      scientificName: "",
      displayName: commonName,
    };
  }

  let scientificName = "";

  try {
    const fishList = await loadFishSpeciesList();
    const fishListEntry = findFishSpeciesListEntry(fishList, fish);
    scientificName =
      typeof fishListEntry?.scientificName === "string"
        ? fishListEntry.scientificName.trim()
        : "";
  } catch {
    scientificName = "";
  }

  if (!scientificName) {
    const catalog = await loadFishDetailsCatalog();
    const detail = resolveSpeciesDetail(catalog, fish, options.waterType);
    scientificName =
      typeof detail?.scientificName === "string"
        ? detail.scientificName.trim()
        : "";
  }

  if (!scientificName || includesScientificName(commonName, scientificName)) {
    return {
      commonName,
      scientificName,
      displayName: commonName,
    };
  }

  return {
    commonName,
    scientificName,
    displayName: `${commonName} (${scientificName})`,
  };
}

export async function resolveFishModalContent(fish, options = {}) {
  const catalog = await loadFishDetailsCatalog();
  const detail = resolveSpeciesDetail(catalog, fish, options.waterType);
  const fishipediaEntry = resolveFishipediaEntry(resolveFishipediaMatchTarget(fish, detail));

  let imageSrc = null;
  if (detail?.image?.provider === "fishipedia" && fishipediaEntry?.slug) {
    imageSrc = await resolveFishipediaImageSrcForSlug(fishipediaEntry.slug);
  }

  return {
    detail,
    imageSrc,
    fishipediaEntry,
  };
}
